/**
 * Sentry for admin errors (MVP_PHASES.md §2). Loaded after first paint, like PostHog, so it never delays the
 * admin's LCP. Errors raised before it loads are queued. No personal data is sent: only the shop ID as a tag.
 */

import { sanitizeUrl, whenIdle } from './analytics';

const DSN = import.meta.env.VITE_SENTRY_DSN;
const MAX_QUEUED = 20;

type Context = Record<string, string>;
type Client = {
  captureException: (error: unknown, hint?: { tags?: Context }) => unknown;
  setTag: (key: string, value: string) => void;
};

type UrlHolder = { url?: unknown; query_string?: unknown; headers?: Record<string, unknown> };
type Crumb = { data?: Record<string, unknown> };
export type ErrorEvent = { request?: UrlHolder; breadcrumbs?: Crumb[]; user?: unknown };

const CRUMB_URL_KEYS = ['url', 'from', 'to'];

/** Sentry `beforeBreadcrumb`: navigation and fetch breadcrumbs carry page URLs with Shopify's launch parameters. */
export function scrubBreadcrumb<T extends Crumb>(crumb: T): T {
  if (!crumb.data) return crumb;
  for (const key of CRUMB_URL_KEYS) if (key in crumb.data) crumb.data[key] = sanitizeUrl(crumb.data[key]);
  return crumb;
}

/** Sentry `beforeSend`: strips the ID token, HMAC and session from every URL, and drops user details. */
export function scrubErrorEvent<T extends ErrorEvent>(event: T): T {
  if (event.request) {
    event.request.url = sanitizeUrl(event.request.url);
    delete event.request.query_string;
    if (event.request.headers?.Referer) event.request.headers.Referer = sanitizeUrl(event.request.headers.Referer);
  }
  event.breadcrumbs?.forEach(scrubBreadcrumb);
  delete event.user;
  return event;
}

let client: Client | null = null;
const queued: { error: unknown; tags?: Context }[] = [];
const pendingTags: Context = {};

/** Report a handled error (for example a failed API call). Safe to call before Sentry has loaded. */
export function reportError(error: unknown, tags?: Context): void {
  if (!DSN) return;
  if (client) client.captureException(error, { tags });
  else if (queued.length < MAX_QUEUED) queued.push({ error, tags });
}

/** Tag later errors with the shop, so they can be grouped per store. The shop ID is our UUID, not personal data. */
export function setErrorShop(shopId: string, plan: string): void {
  Object.assign(pendingTags, { shop_id: shopId, plan });
  if (client) Object.entries(pendingTags).forEach(([key, value]) => client?.setTag(key, value));
}

export function startErrorReporting(): void {
  if (!DSN) return;
  window.addEventListener('error', event => !client && reportError(event.error ?? event.message));
  window.addEventListener('unhandledrejection', event => !client && reportError(event.reason));
  whenIdle(async () => {
    const Sentry = await import('@sentry/browser');
    Sentry.init({
      dsn: DSN,
      release: `unboxugc-admin@${__APP_VERSION__}`,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0,
      beforeSend: event => scrubErrorEvent(event),
      beforeBreadcrumb: crumb => scrubBreadcrumb(crumb),
    });
    client = Sentry;
    Object.entries(pendingTags).forEach(([key, value]) => Sentry.setTag(key, value));
    queued.splice(0).forEach(({ error, tags }) => Sentry.captureException(error, { tags }));
  });
}
